import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAppSelector } from 'hooks';
import { trackPendoEvent } from 'services/PendoService';
import { getAvatarUrl } from 'utils/media';

import { useCompactLobbyHeader } from 'components/SaloonLobby/compactHeader';
import AvatarFrame from 'components/Streaks/AvatarFrame';
import {
  avatarName,
  buildAvatarLadder,
  frameSwitchLine,
  LadderTile,
  selectedFrameLine,
  wornTier
} from 'components/Streaks/model/avatarLadder';
import { sortedMilestones } from 'components/Streaks/model/milestones';
import { FrameTier, StreakSnapshot } from 'components/Streaks/model/types';
import { loadAvatarManifest, useAvatarManifest } from 'components/Streaks/state/useStatusAvatars';
import { useStreakReducedMotion } from 'components/Streaks/useStreakReducedMotion';

import styles from './StreakAvatarPicker.module.scss';

/** What Confirm saves; a field is present only when it changed. */
export interface AvatarPickerChange {
  avatar?: string;
  showFrame?: boolean;
}

type Props = {
  open: boolean;
  snapshot: StreakSnapshot;
  currentAvatar: string;
  onClose: () => void;
  /** Saves the change; resolves to an error message, or null when saved. */
  onSave: (change: AvatarPickerChange) => Promise<string | null>;
};

const TOAST_MS = 6000 + 240;
/** A drag on the handle past this closes the sheet, like Cancel. */
const DRAG_CLOSE_PX = 120;

const CheckIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
    <circle cx='12' cy='12' r='10' fill='#6ddf8b' />
    <path
      d='M7.5 12.5l3 3 6-6.5'
      fill='none'
      stroke='#09110c'
      strokeWidth='2.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const LockIcon = ({ size = 10, color = '#f1e3c7' }: { size?: number; color?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke={color}
    strokeWidth='2.6'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'>
    <rect x='5' y='11' width='14' height='10' rx='2' />
    <path d='M8 11V8a4 4 0 0 1 8 0v3' />
  </svg>
);

const CrossIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.4'
    strokeLinecap='round'
    aria-hidden='true'>
    <path d='M6 6l12 12M18 6L6 18' />
  </svg>
);

/**
 * Change Avatar with the streak frame switch and the status avatars
 * (EN-11164; canvas board 8 phone, board D4 desktop). Only mounted while
 * ops.streaks is on and the player has a snapshot; AvatarPickerModal keeps
 * the old picker otherwise.
 *
 * Every avatar is one tile, locked or not: a locked tile keeps its art, is
 * focusable with `aria-disabled`, and a tap says what unlocks it. Confirm
 * wakes up only when the avatar or the switch differs from what is saved, and
 * saves both; Cancel discards both.
 */
export default function StreakAvatarPicker({
  open,
  snapshot,
  currentAvatar,
  onClose,
  onSave
}: Props) {
  const phone = useCompactLobbyHeader();
  const reducedMotion = useStreakReducedMotion();
  const playerGUID = useAppSelector((s) => s.user.playerGUID);
  const manifest = useAvatarManifest(open);
  const [selected, setSelected] = useState(currentAvatar);
  const [frameOn, setFrameOn] = useState(snapshot.showFrame);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ text: string; n: number; error: boolean } | null>(null);
  const [drag, setDrag] = useState(0);
  const dragStart = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(currentAvatar);
    setFrameOn(snapshot.showFrame);
    setSaving(false);
    setToast(null);
    setDrag(0);
    // Fresh each open: art and sets added to the pool show without a deploy.
    loadAvatarManifest({ fresh: true });
    // Only on open: a snapshot push while it is open keeps the player's edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!toast || toast.error) return undefined;
    const id = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(id);
  }, [toast]);

  const ladder = useMemo(
    () => buildAvatarLadder(snapshot, manifest.status),
    [snapshot, manifest.status]
  );
  const holding = ladder.current;
  const firstMilestone = sortedMilestones(snapshot.milestones)[0];
  const tier = wornTier(holding, frameOn);
  const frameChanged = frameOn !== snapshot.showFrame;
  const avatarChanged = !!selected && selected !== currentAvatar;
  const dirty = frameChanged || avatarChanged;

  const statusOf = useMemo(() => {
    const map = new Map<string, string>();
    ladder.rows.forEach((r) => r.tiles.forEach((t) => t.file && map.set(t.file, r.key)));
    return map;
  }, [ladder]);

  const showToast = useCallback((text: string, error = false) => {
    setToast((t) => ({ text, n: (t?.n ?? 0) + 1, error }));
  }, []);

  const pickTile = (tile: LadderTile, statusKey: string) => {
    if (saving) return;
    if (tile.locked || tile.custom) {
      showToast(tile.lockedMessage);
      if (tile.locked && playerGUID) {
        trackPendoEvent({
          playerGUID,
          eventName: 'locked_avatar_tapped',
          properties: { status: statusKey }
        });
      }
      return;
    }
    setSelected(tile.file);
    setToast(null);
  };

  const close = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const confirm = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setToast(null);
    const change: AvatarPickerChange = {};
    if (avatarChanged) change.avatar = selected;
    if (frameChanged) change.showFrame = frameOn;
    const error = await onSave(change);
    setSaving(false);
    if (error) {
      showToast(error, true);
      return;
    }
    if (playerGUID && change.avatar) {
      trackPendoEvent({
        playerGUID,
        eventName: 'avatar_selected',
        properties: { source: statusOf.get(change.avatar) ?? 'pool' }
      });
    }
    if (playerGUID && change.showFrame !== undefined) {
      trackPendoEvent({
        playerGUID,
        eventName: 'streak_frame_toggled',
        properties: { on: change.showFrame }
      });
    }
    onClose();
  };

  if (!open) return null;

  const selectedName = selected
    ? manifest.avatars.includes(selected) || statusOf.has(selected)
      ? avatarName(selected)
      : 'Current avatar'
    : 'No avatar selected';

  const tileButton = (
    tile: LadderTile,
    statusKey: string,
    size: number,
    frame: FrameTier | null
  ) => {
    const isSelected = !tile.locked && !tile.custom && tile.file === selected;
    return (
      <button
        key={tile.file || `${statusKey}-custom`}
        type='button'
        role='radio'
        className={styles.tile}
        aria-checked={isSelected}
        aria-disabled={tile.locked || tile.custom}
        aria-label={tile.label}
        onClick={() => pickTile(tile, statusKey)}>
        {tile.custom ? (
          <span
            className={styles.mystery}
            style={{ ['--size' as string]: `${size}px` }}
            aria-hidden='true'>
            ?<i>✦</i>
          </span>
        ) : (
          <AvatarFrame tier={frame} size={size} src={getAvatarUrl(tile.file)} alt='' />
        )}
        {isSelected && (
          <span className={styles.badge} aria-hidden='true'>
            <CheckIcon />
          </span>
        )}
        {tile.locked && (
          <span className={`${styles.badge} ${styles.lock}`} aria-hidden='true'>
            <LockIcon />
          </span>
        )}
      </button>
    );
  };

  const tileSize = phone ? 54 : 50;
  const helper =
    'Avatars you unlock stay yours for good, even after a streak ends. The frame shows your current streak.';

  const left = (
    <div className={styles.left}>
      <div className={styles.selected}>
        <AvatarFrame
          tier={tier}
          size={phone ? 64 : 72}
          src={selected ? getAvatarUrl(selected) : undefined}
          alt=''
        />
        <div className={styles.selectedText}>
          <span className={styles.eyebrow}>Selected</span>
          <span className={styles.selectedName}>{selectedName}</span>
          <span className={styles.selectedFrame}>{selectedFrameLine(holding, frameOn)}</span>
        </div>
      </div>
      <div className={styles.switchCard}>
        <span className={styles.switchText}>
          <span id='streak-frame-switch' className={styles.switchLabel}>
            Show my streak frame
          </span>
          <span className={styles.switchSub}>
            {frameSwitchLine(holding, frameOn, firstMilestone)}
          </span>
        </span>
        <button
          type='button'
          role='switch'
          className={styles.switch}
          aria-labelledby='streak-frame-switch'
          aria-checked={frameOn}
          aria-disabled={saving}
          onClick={() => {
            if (saving) return;
            setFrameOn((on) => !on);
            setToast(null);
          }}>
          <span aria-hidden='true' />
        </button>
      </div>
      {!phone && <span className={styles.helper}>{helper}</span>}
    </div>
  );

  const right = (
    <div className={styles.right}>
      <section className={styles.ladder} aria-labelledby='streak-avatars-title'>
        <div className={styles.sectionHead}>
          <h3 id='streak-avatars-title' className={styles.sectionTitle}>
            Streak avatars
          </h3>
          <span className={styles.typewriter}>{ladder.dayLine}</span>
        </div>
        {ladder.rows.map((row) => (
          <div key={row.key} role='radiogroup' aria-label={row.label} className={styles.row}>
            <div className={styles.sectionHead}>
              <span className={styles.rowName}>{row.name}</span>
              <span className={`${styles.chip} ${styles[row.tone]}`}>
                {row.held ? (
                  <svg
                    width='10'
                    height='10'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='3'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'>
                    <path d='M5 12.5l4.5 4.5L19 7' />
                  </svg>
                ) : (
                  <LockIcon size={9} color='currentColor' />
                )}
                {row.chip}
              </span>
            </div>
            {row.bar && (
              <>
                <div
                  className={styles.bar}
                  role='progressbar'
                  aria-valuemin={0}
                  aria-valuemax={row.days}
                  aria-valuenow={Math.min(row.days, Math.round((row.bar.percent * row.days) / 100))}
                  aria-label={row.bar.label}>
                  <span style={{ width: `${row.bar.percent}%` }} />
                </div>
                <span className={styles.barLine}>{row.bar.line}</span>
              </>
            )}
            {row.tiles.length ? (
              <div className={styles.grid}>
                {row.tiles.map((t) => tileButton(t, row.key, tileSize, tier))}
              </div>
            ) : (
              <span className={styles.empty}>Avatars coming soon.</span>
            )}
            {row.note && <span className={styles.note}>{row.note}</span>}
          </div>
        ))}
      </section>

      <section role='radiogroup' aria-labelledby='default-avatars-title' className={styles.row}>
        <div className={styles.sectionHead}>
          <h3 id='default-avatars-title' className={`${styles.sectionTitle} ${styles.quiet}`}>
            Default avatars
          </h3>
          <span className={`${styles.typewriter} ${styles.quiet}`}>Always yours</span>
        </div>
        <div className={styles.grid}>
          {manifest.avatars.map((file) => {
            const name = avatarName(file);
            return tileButton(
              { file, name, locked: false, custom: false, label: name, lockedMessage: '' },
              'pool',
              tileSize,
              tier
            );
          })}
        </div>
      </section>

      {phone && <span className={styles.helper}>{helper}</span>}
    </div>
  );

  const onHandleDown = (e: React.PointerEvent) => {
    dragStart.current = e.clientY;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onHandleMove = (e: React.PointerEvent) => {
    if (dragStart.current === null) return;
    setDrag(Math.max(0, e.clientY - dragStart.current));
  };
  const onHandleUp = () => {
    if (dragStart.current === null) return;
    dragStart.current = null;
    if (drag > DRAG_CLOSE_PX && !saving) {
      onClose();
      return;
    }
    setDrag(0);
  };
  const dragProps = phone
    ? {
        onPointerDown: onHandleDown,
        onPointerMove: onHandleMove,
        onPointerUp: onHandleUp,
        onPointerCancel: onHandleUp
      }
    : {};

  return ReactDOM.createPortal(
    <div
      className={`${styles.backdrop} ${phone ? styles.phone : ''} ${
        reducedMotion ? styles.still : ''
      }`}
      onClick={close}
      data-testid='streak-avatar-picker'>
      <section
        role='dialog'
        aria-modal='true'
        aria-labelledby='change-avatar-title'
        className={`${styles.panel} ${phone ? styles.sheet : styles.dialog}`}
        style={drag ? { transform: `translateY(${drag}px)` } : undefined}
        onClick={(e) => e.stopPropagation()}>
        {phone && (
          <div className={styles.handle} aria-hidden='true' {...dragProps}>
            <span />
          </div>
        )}
        <header className={styles.header} {...dragProps}>
          <div className={styles.titles}>
            <h2 id='change-avatar-title' className={styles.title}>
              Change Avatar
            </h2>
            <span className={styles.subtitle}>Pick an avatar, then confirm.</span>
          </div>
          <button
            type='button'
            className={styles.close}
            aria-label='Close'
            onPointerDown={(e) => e.stopPropagation()}
            onClick={close}>
            <CrossIcon size={16} />
          </button>
        </header>

        <div className={styles.body}>
          {left}
          {right}
        </div>

        <div className={styles.footer}>
          <button type='button' className={`${styles.button} ${styles.cancel}`} onClick={close}>
            Cancel
          </button>
          <button
            type='button'
            className={`${styles.button} ${styles.confirm}`}
            aria-disabled={!dirty || saving}
            onClick={confirm}>
            {saving ? 'Saving…' : 'Confirm avatar'}
          </button>
        </div>

        {toast && (
          <div
            key={toast.n}
            className={`${styles.toast} ${toast.error ? styles.error : ''}`}
            role={toast.error ? 'alert' : 'status'}>
            <span>{toast.text}</span>
            <button
              type='button'
              className={styles.dismiss}
              aria-label='Dismiss'
              onClick={() => setToast(null)}>
              <CrossIcon size={12} />
            </button>
          </div>
        )}
      </section>
    </div>,
    document.body
  );
}
